"use client";

import { useInView, useMotionValue, useSpring } from "motion/react";
import { useCallback, useEffect, useRef } from "react";

interface CountUpProps {
	to: number;
	from?: number;
	direction?: "up" | "down";
	tween?: boolean;
	delay?: number;
	duration?: number;
	className?: string;
	startWhen?: boolean;
	/**
	 * When true (default), if an animation has started, it will finish even if `startWhen`
	 * flips to false mid-flight. This avoids ending on an intermediate value when the
	 * parent turns off the animation gate slightly early.
	 */
	finishTweenWhenStartWhenFalse?: boolean;
	separator?: string;
	onStart?: () => void;
	onEnd?: () => void;
}

export default function CountUp({
	to,
	from = 0,
	direction: _direction = "up",
	tween = false,
	delay = 0,
	duration = 2,
	className = "",
	startWhen = true,
	finishTweenWhenStartWhenFalse = true,
	separator = "",
	onStart,
	onEnd,
}: CountUpProps) {
	const ref = useRef<HTMLSpanElement>(null);
	const onStartRef = useRef<CountUpProps["onStart"]>(onStart);
	const onEndRef = useRef<CountUpProps["onEnd"]>(onEnd);
	useEffect(() => {
		onStartRef.current = onStart;
		onEndRef.current = onEnd;
	}, [onStart, onEnd]);
	/** Spring path: start at `from`, then animate toward `to`. */
	const motionValue = useMotionValue(from);

	const damping = 20 + 40 * (1 / duration);
	const stiffness = 100 * (1 / duration);

	const springValue = useSpring(motionValue, {
		damping,
		stiffness,
	});

	const isInView = useInView(ref, { once: true, margin: "0px" });

	const getDecimalPlaces = (num: number): number => {
		const str = num.toString();
		if (str.includes(".")) {
			const decimals = str.split(".")[1];
			if (Number.parseInt(decimals, 10) !== 0) {
				return decimals.length;
			}
		}
		return 0;
	};

	const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));

	const formatValue = useCallback(
		(latest: number) => {
			const hasDecimals = maxDecimals > 0;

			const options: Intl.NumberFormatOptions = {
				useGrouping: Boolean(separator),
				minimumFractionDigits: hasDecimals ? maxDecimals : 0,
				maximumFractionDigits: hasDecimals ? maxDecimals : 0,
			};

			const formattedNumber = Intl.NumberFormat("en-US", options).format(
				latest,
			);

			return separator
				? formattedNumber.replaceAll(",", separator)
				: formattedNumber;
		},
		[maxDecimals, separator],
	);

	useEffect(() => {
		if (ref.current) {
			// If the animation gate is off, still render the correct value (no tween),
			// otherwise prime the text to the `from` value before animating.
			ref.current.textContent = formatValue(startWhen ? from : to);
		}
	}, [from, startWhen, to, formatValue]);

	const tweenRunningRef = useRef(false);
	useEffect(() => {
		if (!isInView) {
			return;
		}
		// If a tween is already in progress and the start gate flips false, optionally let it finish.
		// Crucially: don't start a second tween run while the first is still running.
		if (
			tween &&
			tweenRunningRef.current &&
			!startWhen &&
			finishTweenWhenStartWhenFalse
		) {
			return;
		}
		if (!startWhen) {
			return;
		}
		if (typeof onStartRef.current === "function") {
			onStartRef.current();
		}

		let rafId = 0;
		const start = () => {
			if (tween) {
				tweenRunningRef.current = true;
				const startValue = from;
				const endValue = to;
				const durationMs = Math.max(0, duration * 1000);
				let startTimestamp: number | null = null;

				const tick = (timestamp: number) => {
					if (startTimestamp === null) {
						startTimestamp = timestamp;
					}
					const elapsed = timestamp - startTimestamp;
					const progress =
						durationMs === 0
							? 1
							: Math.min(1, elapsed / durationMs);
					const nextValue =
						startValue + (endValue - startValue) * progress;

					if (ref.current) {
						ref.current.textContent = formatValue(nextValue);
					}

					if (progress < 1) {
						rafId = window.requestAnimationFrame(tick);
						return;
					}
					tweenRunningRef.current = false;
					if (typeof onEndRef.current === "function") {
						onEndRef.current();
					}
				};

				rafId = window.requestAnimationFrame(tick);
				return;
			}

			motionValue.set(from);
			requestAnimationFrame(() => {
				motionValue.set(to);
			});
		};
		const delayMs = Math.max(0, delay * 1000);
		// Avoid an extra macrotask hop when delay is 0; start on the next RAF.
		const timeoutId = delayMs === 0 ? 0 : window.setTimeout(start, delayMs);
		if (delayMs === 0) {
			rafId = window.requestAnimationFrame(() => start());
		}

		const durationTimeoutId = tween
			? null
			: window.setTimeout(
					() => {
						if (typeof onEndRef.current === "function") {
							onEndRef.current();
						}
					},
					delayMs + duration * 1000,
				);

		return () => {
			// If the tween is running and the caller wants it to finish even when the
			// start gate flips false, don't cancel the RAF.
			const allowFinish =
				tween &&
				finishTweenWhenStartWhenFalse &&
				tweenRunningRef.current &&
				!startWhen;
			if (allowFinish) {
				window.clearTimeout(timeoutId);
				if (durationTimeoutId !== null) {
					window.clearTimeout(durationTimeoutId);
				}
				return;
			}
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
			if (durationTimeoutId !== null) {
				window.clearTimeout(durationTimeoutId);
			}
			tweenRunningRef.current = false;
		};
	}, [
		isInView,
		startWhen,
		finishTweenWhenStartWhenFalse,
		motionValue,
		from,
		to,
		tween,
		delay,
		duration,
		formatValue,
	]);

	useEffect(() => {
		if (tween) {
			return;
		}
		const unsubscribe = springValue.on("change", (latest: number) => {
			if (ref.current) {
				ref.current.textContent = formatValue(latest);
			}
		});

		return () => unsubscribe();
	}, [springValue, formatValue, tween]);

	return <span className={className} ref={ref} />;
}
